/**
 * Managed card template renderer.
 *
 * Fills data-element slots in a pre-built HTML template with Algolia hit data,
 * applying the content mapping and style variables saved in the card_template
 * view setting.
 *
 * Custom template path (old {hit.field} style) is a separate concern — see
 * algolia-hits-component.js for the branch point.
 */

import { __ } from './i18n.js';

/**
 * Read a datapoint out of a hit.
 *
 * Datapoints are normally a record's top-level keys, but the author is indexed as one nested
 * object (an index stores the author once; which part of them a card shows is a display choice),
 * so its values are addressed with a dotted id like "author.display_name" — see
 * tgbl_ss_author_datapoints_from_hit() in includes/admin/settings/ajax.php.
 *
 * A top-level key containing a literal dot wins over the dotted path, so a record that genuinely
 * has a key like "price.usd" keeps working.
 */
export function resolveDatapoint( hit, datapoint ) {
	if ( ! hit || ! datapoint ) return undefined;
	if ( hit[ datapoint ] !== undefined ) return hit[ datapoint ];
	if ( datapoint.indexOf( '.' ) === -1 ) return undefined;

	return datapoint.split( '.' ).reduce(
		( value, key ) => ( value != null && typeof value === 'object' ) ? value[ key ] : undefined,
		hit
	);
}

/**
 * Returns an SVG <use> string referencing the TUI sprite (inlined in wp_footer).
 * Icon names follow the "category/slug" convention, e.g. "system/check".
 * Symbol IDs in the sprite are "tui-{category}-{slug}".
 */
function iconToSvg( name ) {
	if ( ! name ) return '';
	const symbolId = 'tui-' + name.replace( '/', '-' );
	return `<svg class="tgbl-meta-icon" aria-hidden="true" focusable="false" width="1em" height="1em" viewBox="0 0 24 24"><use href="#${ symbolId }"></use></svg>`;
}

const templateCache = new Map();

const CSS_VAR_MAP = {
	bg_color:            '--tgbl-card-bg',
	accent_color:        '--tgbl-card-accent',
	accent_light_color:  '--tgbl-card-accent-light',
	text_color:          '--tgbl-card-text',
	text_emphasis_color: '--tgbl-card-text-emphasis',
	text_on_accent:      '--tgbl-card-text-on-accent',
	font_heading:        '--tgbl-card-font-heading',
	font_normal:         '--tgbl-card-font-normal',
	font_small:          '--tgbl-card-font-small',
	shadow_x:            '--tgbl-card-shadow-x',
	shadow_y:            '--tgbl-card-shadow-y',
	shadow_blur:         '--tgbl-card-shadow-blur',
	shadow_spread:       '--tgbl-card-shadow-spread',
	shadow_color:        '--tgbl-card-shadow-color',
};

/**
 * Returns true when a toggle value is on.
 * Defaults to `defaultOn` when the key has never been set (first load shows all slots).
 */
function toggleOn( content, key, defaultOn = true ) {
	return key in content ? Boolean( content[ key ] ) : defaultOn;
}

/**
 * Whether a slot is on when nobody has ever saved a toggle for it.
 *
 * Normally true: picking a template should show what it can do rather than an empty card the reader
 * has to switch on piece by piece. But a slot ADDED to a template that sites already use has no
 * saved key either, and defaulting THAT on makes it appear unannounced on every existing card. Such
 * a slot carries data-default-off, and stays hidden until someone asks for it.
 *
 * The editor mirrors this in CardTemplateTab.js's defaultsOn(). The two must agree, or the card
 * preview and the rendered card disagree about whether a slot is there.
 */
function elementDefaultsOn( el ) {
	const flag = el.dataset.defaultOff;
	return flag === undefined || flag === 'false';
}

/**
 * CSS-class-safe slug of a mapped hit value, so themes can style the icon
 * per type (e.g. .icon--video { background: … }).
 */
function classSlug( value ) {
	return String( value ).toLowerCase().replace( /[^a-z0-9]+/g, '-' ).replace( /^-+|-+$/g, '' );
}

/**
 * Finds the value-mapping row that applies to this hit for a given config prefix.
 *
 * The shared half of every value-mapped element. A mapped element stores `{prefix}_datapoint` (what
 * to look at) and `{prefix}_mappings` (a JSON array of rows, each keyed by the `value` it matches);
 * only what a caller reads OFF the matched row differs — the icon element wants a glyph or an
 * image, the CTA button wants a label and a link. So the lookup lives here and the interpretation
 * stays with each element.
 *
 * `isUsable` lets a caller reject a row that matches by value but carries nothing it can render, so
 * it falls through to its own static config instead of rendering a blank. The icon element uses it;
 * the button does not, because a row with no label is a meaningful instruction there (keep the
 * static label, just change the link).
 *
 * Returns { row, rawValue } — row may be null while rawValue is set, which is the "datapoint
 * resolved but nothing mapped it" case a caller may still want the raw value for. Returns null when
 * there is no datapoint at all or it resolves to nothing.
 */
function findMappingRow( hit, content, prefix, isUsable = () => true ) {
	const datapoint = content[ prefix + '_datapoint' ] || '';
	if ( ! datapoint ) return null;

	const rawValue = String( resolveDatapoint( hit, datapoint ) ?? '' );
	if ( rawValue === '' ) return null;

	let mappings;
	try { mappings = JSON.parse( content[ prefix + '_mappings' ] || '[]' ); } catch { mappings = []; }
	if ( ! Array.isArray( mappings ) ) mappings = [];

	return {
		row: mappings.find( m => m && m.value === rawValue && isUsable( m ) ) || null,
		rawValue,
	};
}

/**
 * Resolves one icon config section (prefix = element id for the primary
 * section, '{id}_fallback' for the fallback section): a mapping-row match on
 * the section's datapoint wins, else the section's static icon/image.
 *
 * Returns { type: 'icon'|'image', icon?, src?, rawValue } or null.
 */
function resolveIconSection( hit, content, prefix ) {
	const found    = findMappingRow( hit, content, prefix,
		m => ( ( m.type || 'icon' ) === 'image' ? m.image_url : m.icon )
	);
	const rawValue = found?.rawValue ?? '';
	const match    = found?.row ?? null;

	if ( match ) {
		return ( match.type || 'icon' ) === 'image'
			? { type: 'image', src: match.image_url, rawValue }
			: { type: 'icon', icon: match.icon, rawValue };
	}

	const staticType = content[ prefix + '_icon_type' ] || 'icon';
	if ( staticType === 'image' && content[ prefix + '_icon_image_url' ] ) {
		return { type: 'image', src: content[ prefix + '_icon_image_url' ], rawValue };
	}
	if ( staticType === 'icon' && content[ prefix + '_icon_name' ] ) {
		return { type: 'icon', icon: content[ prefix + '_icon_name' ], rawValue };
	}
	return null;
}

/**
 * Resolves the icon element's visual: the primary section first (mapping
 * match, else its static icon), then — when the primary resolves nothing —
 * the fallback section ({id}_fallback_*, its own mapping + static icon).
 *
 * Returns { type: 'icon'|'image', icon?, src?, rawValue } or null when
 * nothing resolves (the slot is then hidden).
 */
function resolveIconVisual( hit, content, id ) {
	const primary = resolveIconSection( hit, content, id );
	if ( primary ) return primary;

	if ( toggleOn( content, id + '_fallback_toggle' ) ) {
		return resolveIconSection( hit, content, id + '_fallback' );
	}
	return null;
}

/**
 * Percent a progress element should show for this hit, or null when it should not render.
 *
 * Deliberately distinguishes "no value" from "zero". users_course_progress resolves to '' for a
 * course the visitor is not enrolled in and to 0 for one they are enrolled in but have not started
 * (see resolveCourseProgress in searchsync-hits-component.js), and those want opposite treatment:
 * no bar at all on a catalog listing versus an empty track on a dashboard. Returning null for the
 * first and 0 for the second is what keeps that distinction visible.
 *
 * The total is a second DATAPOINT (`{prefix}_max_datapoint`), not a number typed into the card
 * settings, because the total it names is per-record: a course with 12 lessons and one with 30
 * cannot share a constant, so any single figure would be wrong for most of the results. Unset means
 * the value is already a percentage — which is the common case and needs no configuration at all.
 *
 * The percentage is clamped, so real data that overshoots its total (105% of a goal) fills the
 * track rather than overflowing it.
 */
function progressPercent( hit, content, prefix ) {
	const datapoint = content[ prefix + '_datapoint' ] || '';
	if ( ! datapoint ) return null;

	const raw = resolveDatapoint( hit, datapoint );
	if ( raw === undefined || raw === null || raw === '' ) return null;

	const value = Number( raw );
	if ( ! Number.isFinite( value ) ) return null;

	const maxDatapoint = content[ prefix + '_max_datapoint' ] || '';
	const rawMax       = maxDatapoint ? resolveDatapoint( hit, maxDatapoint ) : 100;
	const max          = Number( rawMax );

	// A record whose total is missing, non-numeric or zero has nothing for the value to be a
	// fraction OF. Hidden rather than divided by — and rather than falling back to treating the
	// value as a percentage, which would draw a confidently wrong bar (3 lessons as 3%).
	if ( ! Number.isFinite( max ) || max <= 0 ) return null;

	return {
		percent: Math.max( 0, Math.min( 100, Math.round( ( value / max ) * 100 ) ) ),
		value,
		max,
	};
}

/**
 * The progress element's text, with the placeholders a site owner can use in `{prefix}_label`.
 *
 * `{percent}` is the rescaled 0-100 figure the bar itself draws; `{value}` and `{max}` are the raw
 * numbers, so a label can read "3 of 12 lessons" off a source that is not a percentage at all.
 * Saved labels arrive already translated — `progress_label` ends in `_label`, so
 * HitsShortcodeGenerator::translate_card_template_strings picks it up with every other card string.
 */
function progressLabel( content, prefix, resolved ) {
	const template = content[ prefix + '_label' ] || __( '{percent}% complete', 'tangible-searchsync' );

	return String( template )
		.replace( /\{percent\}/g, String( resolved.percent ) )
		.replace( /\{value\}/g, String( resolved.value ) )
		.replace( /\{max\}/g, String( resolved.max ) );
}

/**
 * Fills the progress element: sets the fill width through a custom property, names the bar for
 * assistive technology, and shows or drops the text label.
 *
 * The width rides on `--tgbl-progress` rather than an inline width on the fill node so a theme can
 * reshape the whole thing — a ring, a stepped bar — off the same variable without the renderer
 * knowing which node to size.
 */
function fillProgress( el, hit, content, prefix ) {
	const resolved = progressPercent( hit, content, prefix );

	if ( ! resolved ) {
		el.style.display = 'none';
		return;
	}

	const text = progressLabel( content, prefix, resolved );

	el.style.setProperty( '--tgbl-progress', resolved.percent + '%' );
	el.setAttribute( 'role', 'progressbar' );
	el.setAttribute( 'aria-valuenow', String( resolved.percent ) );
	el.setAttribute( 'aria-valuemin', '0' );
	el.setAttribute( 'aria-valuemax', '100' );
	// role="progressbar" needs an accessible name, and its contents are not announced — so the label
	// is set here whether or not it is shown, and a bar with the text hidden still reads as
	// "45% complete" rather than as an unnamed progress bar.
	el.setAttribute( 'aria-label', text );

	const labelEl = el.querySelector( '.progress__label' );
	if ( ! labelEl ) return;

	// Off by default: the point of a bar is to replace the printed number, so the text is opt-in.
	if ( toggleOn( content, prefix + '_show_label', false ) ) {
		labelEl.textContent = text;
	} else {
		labelEl.remove();
	}
}

/**
 * Parses and caches the template HTML. Returns the root element.
 */
function getTemplateRoot( templateHtml ) {
	if ( ! templateCache.has( templateHtml ) ) {
		const div       = document.createElement( 'div' );
		div.innerHTML   = templateHtml;
		templateCache.set( templateHtml, div.firstElementChild );
	}
	return templateCache.get( templateHtml );
}

const DIMENSION_KEYS = new Set( [
	'font_heading', 'font_normal', 'font_small',
	'shadow_x', 'shadow_y', 'shadow_blur', 'shadow_spread',
] );

function resolveDimensionValue( v ) {
	try {
		const parsed = JSON.parse( v );
		if ( parsed !== null && typeof parsed === 'object' && 'value' in parsed ) {
			return `${ parsed.value }${ parsed.unit }`;
		}
	} catch {}
	const n = parseFloat( v );
	return isNaN( n ) ? v : `${ n }px`;
}

/**
 * Builds an inline style string of CSS custom properties from the styles object.
 */
function buildStyleAttr( styles ) {
	return Object.entries( CSS_VAR_MAP )
		.filter( ( [ key ] ) => styles[ key ] )
		.map( ( [ key, varName ] ) => {
			const val = DIMENSION_KEYS.has( key )
				? resolveDimensionValue( styles[ key ] )
				: styles[ key ];
			return `${ varName }: ${ val }`;
		} )
		.join( '; ' );
}

/**
 * Fills the cloned card DOM with hit data according to the content mapping.
 */
/**
 * A metadata value as a reader should see it.
 *
 * Dates are indexed as unix timestamps — that is what sorting and ranges need — but a card that
 * prints "1779370409" under a title is broken to anyone reading it. A metadata slot whose value
 * is a ten-digit timestamp is shown as a date in the visitor's locale. Only metadata: a number in
 * a heading or a tag is a number. Anything else passes through untouched.
 */
function formatForDisplay( value, tag ) {
	if ( tag !== 'dd' ) return value;
	const text = String( value ).trim();
	if ( ! /^\d{10}$/.test( text ) ) return value;
	const date = new Date( Number( text ) * 1000 );
	if ( Number.isNaN( date.getTime() ) ) return value;
	return date.toLocaleDateString( undefined, { year: 'numeric', month: 'short', day: 'numeric' } );
}

function fillCard( card, hit, content, facetMappingsByAttribute = {} ) {
	card.querySelectorAll( '[data-element]' ).forEach( el => {
		const id  = el.dataset.element;
		const tag = el.tagName.toLowerCase();

		if ( id === 'root' ) return;

		const isOn = toggleOn( content, id + '_toggle', elementDefaultsOn( el ) );

		if ( ! isOn ) {
			el.style.display = 'none';
			return;
		}

		// Progress bar: a numeric datapoint drawn as a filled track instead of a printed number.
		// Handled before the container check below because its inner nodes carry no data-element
		// (they are chrome, not slots), so it would otherwise fall through to the generic
		// text-filling path and have the raw number written over its own markup.
		if ( id === 'progress' ) {
			fillProgress( el, hit, content, id );
			return;
		}

		// Icon slot: static or value-mapped type icon. Hidden entirely when
		// nothing resolves, so cards without icon config stay unchanged.
		if ( id === 'icon' ) {
			const visual = resolveIconVisual( hit, content, id );
			if ( ! visual ) {
				el.style.display = 'none';
				return;
			}
			if ( visual.type === 'image' ) {
				const img = document.createElement( 'img' );
				img.src = visual.src;
				img.alt = '';
				el.innerHTML = '';
				el.appendChild( img );
			} else {
				el.innerHTML = iconToSvg( visual.icon );
			}
			// Per-value modifier class so themes can style the icon per type.
			if ( visual.rawValue !== '' ) {
				el.classList.add( `${ el.className.split( ' ' )[ 0 ] || 'icon' }--${ classSlug( visual.rawValue ) }` );
			}
			return;
		}

		// Container elements (those with child data-element slots) only need
		// toggle handling — their children populate themselves.
		// Exception: ul/ol containers with a _datapoint are "dynamic list" elements
		// (e.g. tag cloud) — populate their <li> children from the hit array value.
		if ( el.querySelector( '[data-element]' ) ) {
			if ( tag === 'ul' || tag === 'ol' ) {
				const datapoint = content[ id + '_datapoint' ] || '';
				const rawVal    = datapoint ? resolveDatapoint( hit, datapoint ) : undefined;
				const values    = Array.isArray( rawVal )
					? rawVal.map( v => String( v ) ).filter( Boolean )
					: [];

				// The template's <li>s are PLACEHOLDERS — they exist only to carry the styling and to
				// give the editor sample chips to show (assets/src/hits-templates/template.html ships
				// two for the tag cloud, "WordPress" and "JavaScript", and one for badges). They must
				// always be cleared, whether or not there is anything to replace them with.
				//
				// This block used to do nothing unless `values.length`, so a record with no tags kept
				// the placeholders and rendered them as empty coloured pills — two of them for the tag
				// cloud, which is exactly the count of placeholders in the template. Reported
				// 2026-08-03. Four paths reached that state: the attribute missing from the record, a
				// non-array value, an array of only empty strings, and no datapoint configured at all.
				const liTemplate = el.querySelector( 'li' );
				const liClass    = liTemplate ? liTemplate.className : '';
				Array.from( el.querySelectorAll( 'li' ) ).forEach( li => li.remove() );

				// Nothing to show: hide the whole list rather than leave an empty one, matching the icon
				// slot above ("hidden entirely when nothing resolves") so the list contributes no gap.
				if ( ! values.length ) {
					el.style.display = 'none';
					return;
				}

				values.forEach( val => {
					const li       = document.createElement( 'li' );
					li.className   = liClass;
					li.textContent = val;
					el.appendChild( li );
				} );
			}
			return;
		}

		let datapoint = content[ id + '_datapoint' ] || '';

		// Img / li elements: fall back to parent container's datapoint key for
		// configs saved before the key was corrected to use the child's own id.
		if ( ! datapoint && ( tag === 'img' || tag === 'li' ) ) {
			const parentId = el.parentElement?.closest( '[data-element]' )?.dataset.element;
			if ( parentId ) datapoint = content[ parentId + '_datapoint' ] || '';
		}

		let value;
		if ( datapoint === 'custom' ) {
			let parts;
			try { parts = JSON.parse( content[ id + '_custom_content' ] || '[]' ); } catch { parts = []; }
			if ( ! Array.isArray( parts ) ) parts = [];
			value = parts.map( p => {
				if ( p.type === 'text' ) return p.value || '';
				if ( ! p.field ) return '';
				const raw    = String( resolveDatapoint( hit, p.field ) ?? '' );
				const fMap   = facetMappingsByAttribute[ p.field ];
				return ( fMap && fMap[ raw ] != null ) ? fMap[ raw ] : raw;
			} ).join( '' );
		} else {
			const rawValue = datapoint ? String( resolveDatapoint( hit, datapoint ) ?? '' ) : '';
			const attrMap  = datapoint ? facetMappingsByAttribute[ datapoint ] : null;
			value          = ( attrMap && attrMap[ rawValue ] != null ) ? attrMap[ rawValue ] : rawValue;
		}

		if ( tag === 'img' ) {
			const useStatic = ( content[ 'image_source' ] || 'indexed_data' ) === 'static_image';
			const src       = useStatic
				? ( content[ 'image_fallback_url' ] || '' )
				: ( value || content[ 'image_fallback_url' ] || '' );
			// No image at all: the container stays (on the row it is the colour block a thumbnail
			// would fill), the <img> goes, because an <img> with an empty src draws a broken-image
			// glyph in most browsers.
			if ( src === '' ) {
				el.style.display = 'none';
				return;
			}
			el.setAttribute( 'src', src );

		} else if ( tag === 'button' ) {
			// Per-value overrides. A mapping row whose `value` matches what this button's datapoint
			// resolves to replaces the static label and/or link, and can drop the button entirely.
			// The motivating case is a course card whose CTA reads "Start course", "Continue" or
			// "View certificate" depending on the viewer's own progress — none of which can be
			// indexed, since all three are per-user (see the virtual datapoints in
			// includes/utils/user-state-labels.php).
			//
			// A button with no datapoint, or one whose value matches no row, keeps exactly the
			// static behaviour it had before mappings existed — which is why adding this changed no
			// existing card template.
			const mapped = findMappingRow( hit, content, id )?.row ?? null;

			// `hide` wins over everything else on the row: a catalog that offers no action at all
			// for, say, a course the visitor cannot enrol in wants no button rather than a
			// disabled-looking one. Hidden the same way every other unresolved slot is, so the card
			// contributes no empty footer where the button used to sit.
			if ( mapped?.hide ) {
				el.style.display = 'none';
				return;
			}

			// Convert to an anchor for proper link behaviour.
			// Saved labels arrive already translated (server-side, via the
			// translation provider); only the default falls back to gettext here.
			// A row's own label is translated the same way — see
			// HitsShortcodeGenerator::translate_card_template_strings, which reaches into the
			// mapping rows for exactly this key.
			const label   = mapped?.label || content[ id + '_label' ] || __( 'Learn More', 'tangible-searchsync' );
			const linkKey = mapped?.link  || content[ id + '_link' ]  || 'post_link';
			// 'post_link' is the legacy sentinel for the default permalink fallback chain. The
			// editor no longer offers it — it duplicated the real `permalink` attribute under a
			// key no record this plugin writes actually has — and stored values are rewritten by
			// tgbl_ss_migrate_card_template_post_link(). Kept because it is also what an UNSET
			// link falls back to above, and because card_template JSON can arrive from a theme or
			// an export the migration never touched. Any other value is a hit attribute key; note
			// both branches end in the same fallback chain, which is what makes the rewrite from
			// 'post_link' to 'permalink' behaviour-preserving.
			const postLink = linkKey === 'post_link'
				? ( hit.post_link || hit.permalink || hit.url || '#' )
				: ( String( resolveDatapoint( hit, linkKey ) ?? '' ) || hit.post_link || hit.permalink || hit.url || '#' );
			const a        = document.createElement( 'a' );
			a.className    = el.className || 'cta';
			a.dataset.element = id;
			a.textContent  = label;
			a.href         = postLink;
			el.replaceWith( a );

		} else if ( value === '' ) {
			// No value for this record: hide the slot instead of rendering an empty one.
			//
			// Visible in the same 2026-08-03 report as the empty tag pills: a <dd> meta item whose
			// datapoint resolved to '' still drew its configured icon below, so a card missing (say)
			// a delivery-mode value showed a bare clock glyph with no text after it. An empty title or
			// description likewise left a box contributing its own margin.
			//
			// Safe for elements with no datapoint configured too: `el.textContent = value` below would
			// have blanked whatever static text the template shipped anyway, so there is nothing to
			// preserve by leaving them visible. Presence is governed by the `_toggle` check at the top
			// of this function; this only suppresses slots that are on but have nothing to say.
			el.style.display = 'none';

		} else {
			el.textContent = formatForDisplay( value, tag );

			// The title as the link to the post. `heading_link` is 'post_link' (the permalink chain,
			// as the button uses) or a datapoint holding a URL; unset, the title stays plain text, as
			// every card saved before this option existed expects.
			if ( id === 'heading' && content.heading_link ) {
				const href = content.heading_link === 'post_link'
					? ( hit.post_link || hit.permalink || hit.url || '' )
					: String( resolveDatapoint( hit, content.heading_link ) ?? '' );
				if ( href ) {
					const a        = document.createElement( 'a' );
					a.href         = href;
					a.textContent  = el.textContent;
					el.textContent = '';
					el.appendChild( a );
				}
			}

			if ( tag === 'dd' ) {
				const iconType = content[ id + '_icon_type' ] || 'icon';
				const iconName = iconType === 'icon'  ? ( content[ id + '_icon_name'      ] || '' ) : '';
				const imageUrl = iconType === 'image' ? ( content[ id + '_icon_image_url' ] || '' ) : '';

				if ( iconName || imageUrl ) {
					const text = el.textContent;
					el.innerHTML = '';

					if ( iconName ) {
						const wrap = document.createElement( 'span' );
						wrap.className = 'tgbl-meta-icon';
						wrap.innerHTML = iconToSvg( iconName );
						el.appendChild( wrap );
					} else if ( imageUrl ) {
						const img = document.createElement( 'img' );
						img.src       = imageUrl;
						img.alt       = '';
						img.className = 'tgbl-meta-icon-img';
						el.appendChild( img );
					}

					const textEl = document.createElement( 'span' );
					textEl.textContent = text;
					el.appendChild( textEl );
				}
			}
		}
	} );

	// Hide the metadata dl when all its children are hidden.
	const meta = card.querySelector( '[data-element="metadata"]' );
	if ( meta ) {
		const anyVisible = Array.from( meta.querySelectorAll( '[data-element]' ) )
			.some( child => child.style.display !== 'none' );
		if ( ! anyVisible ) meta.style.display = 'none';
	}
}

/**
 * Renders a hit card using the managed slot-based template.
 *
 * Returns an htm/Preact element suitable for the InstantSearch `templates.item`
 * callback. The card HTML is injected via dangerouslySetInnerHTML to preserve
 * the full template markup (including CSS classes, data attributes, etc.).
 *
 * @param {Object}   hit          Algolia hit object.
 * @param {Object}   cardTemplate Parsed card_template JSON { template, content, styles }.
 * @param {string}   templateHtml Raw HTML of the template file.
 * @param {Function} html         InstantSearch htm tagged-template function.
 */
/**
 * For inline cards, wraps every direct child except the image in a div.card-body so
 * the image and content area can be laid out as a flex row without each content
 * element becoming its own horizontal flex item.
 */
function wrapInlineCardBody( card ) {
	const image = card.querySelector( ':scope > .image' );
	const body  = document.createElement( 'div' );
	body.className = 'card-body';
	Array.from( card.children ).forEach( child => {
		if ( child !== image ) body.appendChild( child );
	} );
	card.appendChild( body );
}

export function renderManagedCardTemplate( hit, cardTemplate, templateHtml, html, facetMappingsByAttribute = {} ) {
	const { content = {}, styles = {} } = cardTemplate;

	const root = getTemplateRoot( templateHtml );
	const card = root.cloneNode( true );

	fillCard( card, hit, content, facetMappingsByAttribute );

	if ( card.dataset.layout === 'inline' ) {
		wrapInlineCardBody( card );
	}

	const styleAttr = buildStyleAttr( styles );
	if ( styleAttr ) {
		card.setAttribute( 'style', ( card.getAttribute( 'style' ) || '' ) + styleAttr );
	}

	const rendered = card.outerHTML;

	return html`<div class="tgbl-ss-card-wrap" dangerouslySetInnerHTML=${{ __html: rendered }}></div>`;
}
